import React, { useState, useEffect, useCallback } from 'react';
import { subscribeRoom, leaveRoom } from '../shared/roomTx';
import { startChainGame, updatePlayerOrder, submitOrderPreference, submitOrderOk } from '../shared/chainRoomTx';
import { FL_ROOM_STATUS, FIVE_LEAGUE, FL_GAME_MODE, CHAIN_SETTINGS } from '../shared/fiveLeague';
import { CyberBackground, NeonText, CyberButton, GlowingBorder } from '../components/CyberEffects';
import './ChainOrder.css';

// 順番決めの制限時間（秒）
const ORDER_TIME_LIMIT = 60;

function ChainOrder({ navigate, playerId, roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [localOrder, setLocalOrder] = useState([]);
  const [remainingTime, setRemainingTime] = useState(ORDER_TIME_LIMIT);
  const [myPreference, setMyPreference] = useState(null); // null = おまかせ, 0-5 = 希望順
  const [myOk, setMyOk] = useState(false);

  // ルーム購読
  useEffect(() => {
    if (!roomId) {
      navigate('home');
      return;
    }

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      if (!data) {
        setError('ルームが見つかりません');
        return;
      }

      // このプレイヤーがルームに参加しているか確認
      const isPlayer = data.players?.some(p => p.playerId === playerId);
      if (!isPlayer) {
        setError('このルームに参加していません');
        return;
      }

      // チェーンモードでない場合はWait画面へ
      if (data.gameMode !== FL_GAME_MODE.CHAIN) {
        navigate('wait', { roomId });
        return;
      }

      setRoom(data);

      // 初回のみローカル順序を設定
      if (localOrder.length === 0 && data.playerOrder?.length > 0) {
        setLocalOrder([...data.playerOrder]);
      }

      // 自分の投票状態を復元
      if (data.orderVotes?.[playerId]) {
        setMyPreference(data.orderVotes[playerId].preference);
        setMyOk(data.orderVotes[playerId].ok || false);
      }

      // ゲーム開始したらChainGame画面へ
      if (data.status === FL_ROOM_STATUS.PLAYING) {
        navigate('chain-game', { roomId });
      }
    });

    return () => unsub();
  }, [roomId, playerId, navigate, localOrder.length]);

  // タイマー
  useEffect(() => {
    if (!room?.orderDeadline) return;

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((room.orderDeadline - Date.now()) / 1000));
      setRemainingTime(remaining);

      // タイムアウトでホストなら自動開始
      if (remaining === 0 && room.hostId === playerId && !starting) {
        handleStartGame();
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [room?.orderDeadline, room?.hostId, playerId, starting]);

  // 希望順を提出
  const handlePreference = async (preference) => {
    setMyPreference(preference);
    await submitOrderPreference(roomId, playerId, preference);
  };

  // OKを提出
  const handleOk = async () => {
    setMyOk(true);
    await submitOrderOk(roomId, playerId);
  };

  // 順番入れ替え（ホストのみ）
  const handleSwap = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const newOrder = [...localOrder];
    [newOrder[fromIndex], newOrder[toIndex]] = [newOrder[toIndex], newOrder[fromIndex]];
    setLocalOrder(newOrder);
    // Firestoreに反映
    updatePlayerOrder(roomId, playerId, newOrder);
  };

  // シャッフル
  const handleShuffle = async () => {
    const newOrder = [...localOrder];
    for (let i = newOrder.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newOrder[i], newOrder[j]] = [newOrder[j], newOrder[i]];
    }
    setLocalOrder(newOrder);
    await updatePlayerOrder(roomId, playerId, newOrder);
  };

  // ゲーム開始
  const handleStartGame = async () => {
    if (!room || starting) return;

    setStarting(true);
    setError('');

    // カスタム順序をインデックス配列に変換
    const orderIndices = localOrder.map(pid =>
      room.playerOrder.indexOf(pid)
    );

    const result = await startChainGame(roomId, playerId, orderIndices);
    if (!result.ok) {
      if (result.reason === 'not-enough-players') {
        setError(`最低${FIVE_LEAGUE.MIN_PLAYERS}人必要です`);
      } else if (result.reason === 'not-all-ready') {
        setError('全員が準備完了していません');
      } else {
        setError('ゲームを開始できませんでした: ' + result.reason);
      }
      setStarting(false);
    }
  };

  const handleLeave = async () => {
    await leaveRoom(roomId, playerId);
    navigate('home');
  };

  const handleBack = () => {
    navigate('wait', { roomId });
  };

  if (loading) {
    return (
      <div className="chain-order-container">
        <CyberBackground showParticles={true} />
        <div className="loading-spinner">読み込み中...</div>
      </div>
    );
  }

  if (error && !room) {
    return (
      <div className="chain-order-container">
        <CyberBackground showParticles={true} />
        <p className="error-message">{error}</p>
        <button className="btn-secondary" onClick={() => navigate('home')}>
          ホームに戻る
        </button>
      </div>
    );
  }

  if (!room) return null;

  const isHost = room.hostId === playerId;
  const controllers = room.players?.filter(p => p.role !== 'display') || [];
  const answerCount = room.config?.answerCount || CHAIN_SETTINGS.ANSWER_COUNT_5;
  const timeLimit = answerCount === 10 ? CHAIN_SETTINGS.TIME_LIMIT_10 : CHAIN_SETTINGS.TIME_LIMIT_5;

  // localOrderからプレイヤー情報を取得
  const orderedPlayers = localOrder
    .map(pid => controllers.find(p => p.playerId === pid))
    .filter(Boolean);

  // 投票状況
  const votes = room.orderVotes || {};
  const okCount = Object.values(votes).filter(v => v.ok).length;
  const totalPlayers = controllers.length;
  const canStart = okCount >= Math.ceil(totalPlayers / 2) || okCount === totalPlayers;

  // 折り返しパターンをプレビュー
  const turnPattern = generateTurnPattern(orderedPlayers.length, Math.min(answerCount, 6));

  return (
    <div className="chain-order-container animate-fade-in">
      <CyberBackground showParticles={true} />

      <div className="chain-order-content">
        <button className="back-btn" onClick={handleBack}>
          ← 戻る
        </button>

        <div className="chain-order-header">
          <h1 className="chain-order-title">
            <NeonText color="pink" size="lg">
              ⚡ アンサー
              <br />
              チェーン
            </NeonText>
          </h1>
          <p className="chain-order-subtitle">
            <NeonText color="blue" size="sm">
              {answerCount}問 / {timeLimit}秒
            </NeonText>
          </p>
        </div>

        {/* タイマー */}
        <div className="order-timer">
          <span className={`timer-value ${remainingTime <= 10 ? 'warning' : ''}`}>
            {remainingTime}
          </span>
          <span className="timer-label">秒で開始</span>
        </div>

        {/* 希望順ボタン */}
        <GlowingBorder color="accent" className="preference-section">
          <p className="preference-label">あなたの希望は？</p>
          <div className="preference-buttons">
            {orderedPlayers.map((_, i) => (
              <button
                key={i}
                className={`preference-btn ${myPreference === i ? 'selected' : ''}`}
                onClick={() => handlePreference(i)}
                disabled={myOk}
              >
                {i + 1}番目
              </button>
            ))}
            <button
              className={`preference-btn omakase ${myPreference === null ? 'selected' : ''}`}
              onClick={() => handlePreference(null)}
              disabled={myOk}
            >
              おまかせ
            </button>
          </div>

          {!myOk ? (
            <CyberButton
              variant="primary"
              className="ok-btn"
              onClick={handleOk}
            >
              ✓ この順番でOK
            </CyberButton>
          ) : (
            <p className="ok-confirmed">✓ OK済み</p>
          )}
        </GlowingBorder>

        {/* プレイヤー順序と投票状況 */}
        <div className="player-order-list">
          {orderedPlayers.map((player, index) => {
            const vote = votes[player.playerId];
            const wantsThis = vote?.preference === index;

            return (
              <GlowingBorder
                key={player.playerId}
                color={wantsThis ? 'accent' : vote?.ok ? 'blue' : 'purple'}
                className={`player-order-card ${isHost ? 'draggable' : ''}`}
              >
                <div className="player-order-number">{index + 1}</div>
                <div className="player-order-info">
                  <span className="player-order-name">
                    {player.nickname}
                    {player.playerId === room.hostId && <span className="host-badge">👑</span>}
                    {player.playerId === playerId && <span className="me-badge">（自分）</span>}
                  </span>
                  <span className="player-vote-status">
                    {vote?.ok ? '✓ OK' : vote?.preference !== undefined ?
                      (vote.preference === null ? 'おまかせ' : `${vote.preference + 1}番希望`) :
                      '考え中...'}
                  </span>
                </div>
                {isHost && (
                  <div className="player-order-arrows">
                    <button
                      className="arrow-btn"
                      onClick={() => handleSwap(index, Math.max(0, index - 1))}
                      disabled={index === 0}
                    >
                      ▲
                    </button>
                    <button
                      className="arrow-btn"
                      onClick={() => handleSwap(index, Math.min(orderedPlayers.length - 1, index + 1))}
                      disabled={index === orderedPlayers.length - 1}
                    >
                      ▼
                    </button>
                  </div>
                )}
              </GlowingBorder>
            );
          })}
        </div>

        {/* OK状況 */}
        <div className="ok-status">
          <span className="ok-count">{okCount} / {totalPlayers} OK</span>
          {canStart && <span className="can-start">開始可能！</span>}
        </div>

        {/* 折り返しプレビュー */}
        <div className="turn-preview">
          <span className="turn-preview-label">順番:</span>
          <div className="turn-preview-pattern">
            {turnPattern.map((slot, i) => (
              <span key={i} className="turn-slot">
                {orderedPlayers[slot]?.nickname?.charAt(0) || '?'}
                {i < turnPattern.length - 1 && <span className="turn-arrow">→</span>}
              </span>
            ))}
            {answerCount > 6 && <span className="turn-more">...</span>}
          </div>
        </div>

        {error && <p className="error-message">{error}</p>}

        <div className="chain-order-actions">
          {isHost && (
            <>
              <CyberButton
                variant="primary"
                className="shuffle-btn"
                onClick={handleShuffle}
              >
                🎲 シャッフル
              </CyberButton>
              <CyberButton
                variant="accent"
                className="start-btn"
                onClick={handleStartGame}
                disabled={starting || orderedPlayers.length < FIVE_LEAGUE.MIN_PLAYERS}
              >
                {starting ? '開始中...' : canStart ? '🚀 ゲームスタート！' : `⏳ ${okCount}/${totalPlayers} OK待ち`}
              </CyberButton>
            </>
          )}

          <button className="cyber-btn-secondary leave-btn" onClick={handleLeave}>
            退出する
          </button>
        </div>
      </div>
    </div>
  );
}

// 折り返しパターン生成（プレビュー用）
function generateTurnPattern(playerCount, previewCount) {
  const pattern = [];
  let forward = true;
  let pos = 0;

  for (let i = 0; i < previewCount; i++) {
    pattern.push(pos);

    if (forward) {
      if (pos >= playerCount - 1) {
        forward = false;
      } else {
        pos++;
      }
    } else {
      if (pos <= 0) {
        forward = true;
      } else {
        pos--;
      }
    }
  }
  return pattern;
}

export default ChainOrder;
